/**
 * Redirect storage utility for handling post-login redirects
 * Stores the intended destination when user needs to login first
 */

const REDIRECT_KEY = 'baoafrik_redirect_urgit l';

/**
 * Validate if a URL is safe for navigation
 * @param url URL to validate
 * @returns true if URL is valid and safe, false otherwise
 */
function isValidRedirectUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Trim whitespace
  url = url.trim();

  // Empty string check
  if (!url) {
    return false;
  }

  // Must start with / for relative URLs or be a valid absolute URL
  if (!url.startsWith('/') && !url.startsWith('http://') && !url.startsWith('https://')) {
    return false;
  }

  // Prevent javascript: protocols and other dangerous schemes
  if (url.toLowerCase().startsWith('javascript:') ||
    url.toLowerCase().startsWith('data:') ||
    url.toLowerCase().startsWith('vbscript:')) {
    return false;
  }

  // Basic URL structure validation for absolute URLs
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      new URL(url);
    } catch {
      return false;
    }
  }

  return true;
}

export class RedirectStorage {
  /**
   * Store the current URL that user should be redirected to after login
   */
  static setRedirectUrl(url: string): void {
    try {
      localStorage.setItem(REDIRECT_KEY, url);
    } catch (error) {
      console.warn('Failed to store redirect URL:', error);
    }
  }

  /**
   * Get the stored redirect URL
   * @returns Valid redirect URL or null if invalid/not found
   */
  static getRedirectUrl(): string | null {
    try {
      const url = localStorage.getItem(REDIRECT_KEY);
      return url && isValidRedirectUrl(url) ? url : null;
    } catch (error) {
      console.warn('Failed to get redirect URL:', error);
      return null;
    }
  }

  /**
   * Clear the stored redirect URL
   */
  static clearRedirectUrl(): void {
    try {
      localStorage.removeItem(REDIRECT_KEY);
    } catch (error) {
      console.warn('Failed to clear redirect URL:', error);
    }
  }

  /**
   * Handle redirect - if there's a stored URL, navigate to it and clear it
   * @param navigate React Router navigate function
   * @param fallbackUrl URL to use if no redirect URL is stored
   */
  static handleRedirect(navigate: (url: string) => void, fallbackUrl: string = '/'): void {
    const redirectUrl = this.getRedirectUrl();

    if (redirectUrl && isValidRedirectUrl(redirectUrl)) {
      this.clearRedirectUrl();
      navigate(redirectUrl);
    } else {
      // Ensure fallback URL is also valid
      const safeFallback = isValidRedirectUrl(fallbackUrl) ? fallbackUrl : '/';
      navigate(safeFallback);
    }
  }

  /**
   * Store current page URL for post-login redirect
   * Call this when user needs to login but and they have to be brought back afterward
   */
  static storeCurrentLocation(): void {
    if (typeof window !== 'undefined') {
      this.setRedirectUrl(window.location.pathname + window.location.search);
    }
  }
}
